import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaSearch, FaMapMarkerAlt, FaShareAlt, FaDirections, FaWifi, FaWind, FaUtensils } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import RestaurantDetail from "./RestaurantDetail";
import { APP_URLS } from "../config/urls.config";
import { safeScrollToTop, safeScrollBy } from "../utils/scrollGuard";
import { useScrollLock } from "../context/ScrollLockContext";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./RestaurantsPageStyles.css";

const normalizeLocationText = (value = "") =>
  value
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const doesRestaurantMatchLocation = (restaurant, locationName) => {
  const normalizedLocation = normalizeLocationText(locationName);
  if (!normalizedLocation) return false;

  const searchableFields = [
    restaurant?.city,
    restaurant?.state,
    restaurant?.address,
    restaurant?.name,
    restaurant?.location,
  ]
    .map((value) => normalizeLocationText(value))
    .filter(Boolean);

  return searchableFields.some(
    (field) =>
      field.includes(normalizedLocation) ||
      normalizedLocation.includes(field),
  );
};


// Restaurants Page - Premium Compact Search (130px) - Matches HomePage
const RestaurantsPage = () => {
  const navigate = useNavigate();
  const { unlockScroll } = useScrollLock();
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCuisine, setSelectedCuisine] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem("user")) || null;

  // Table search state
  const [searchedTables, setSearchedTables] = useState([]);
  const [showTables, setShowTables] = useState(false);

  // Enhanced search form state
  const [searchFormData, setSearchFormData] = useState({
    restaurant: "",
    guests: "",
    bookingDate: "",
    timeSlot: "",
  });

  const [searchErrors, setSearchErrors] = useState({});
  const [restaurantPhotos, setRestaurantPhotos] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();
  const cardRefs = useRef({});
  const [focusedRestaurantId, setFocusedRestaurantId] = useState(null);
  const [highlightedRestaurantId, setHighlightedRestaurantId] = useState(null);
  const [foodTypeFilter, setFoodTypeFilter] = useState("All"); // "All", "Pure Veg", "Non-Veg"
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedRestaurantTypes, setSelectedRestaurantTypes] = useState([]);
  const [sortBy, setSortBy] = useState("popularity");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const activeFilterCount =
    selectedCuisines.length +
    selectedRestaurantTypes.length +
    (foodTypeFilter !== "All" ? 1 : 0);

  // Handle responsive layout on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile && showMobileFilters) {
      setShowMobileFilters(false);
    }
  }, [isMobile, showMobileFilters]);

  useEffect(() => {
    if (!showMobileFilters) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showMobileFilters]);

  // Fetch user food preference and auto-filter
  useEffect(() => {
    const fetchUserPreferences = async () => {
      if (currentUser?.user_id) {
        try {
          const response = await axios.get(
            `${APP_URLS.PROFILE}/preferences/${currentUser.user_id}`,
          );

          if (response.data?.foodPreference) {
            const preference = response.data.foodPreference;
            // Map backend preference to frontend filter
            if (preference === "Vegetarian") {
              setFoodTypeFilter("Pure Veg");
            } else if (preference === "Non-Vegetarian") {
              setFoodTypeFilter("Non-Veg");
            }
            // If preference is "Both" or anything else, leave as "All"
          }
        } catch (error) {
          // Silently fail - user can still filter manually
        }
      }
    };

    fetchUserPreferences();
  }, [currentUser]);

  // Fetch all restaurant photos on component mount
  useEffect(() => {
    if (restaurants && restaurants.length > 0) {
      restaurants.forEach((restaurant) => {
        if (restaurant?.restaurantId) {
          fetchRestaurantPhotos(restaurant.restaurantId);
        }
      });
    }
  }, [restaurants]);

  // Dynamic time slots generation based on selected restaurant's timing
  const generateTimeSlots = (selectedRestaurant = null) => {
    if (!selectedRestaurant) {
      // Default time slots when no restaurant is selected (range format: "1pm to 2pm")
      const slots = [];
      for (let hour = 10; hour <= 22; hour++) {
        const currentHour = hour > 12 ? hour - 12 : hour;
        const currentPeriod = hour >= 12 ? "pm" : "am";

        const nextHour = hour + 1;
        const displayNextHour = nextHour > 12 ? nextHour - 12 : nextHour;
        const nextPeriod = nextHour >= 12 ? "pm" : "am";

        slots.push(
          `${currentHour}${currentPeriod} to ${displayNextHour}${nextPeriod}`,
        );
      }
      return slots;
    }

    const slots = [];
    const openTime = selectedRestaurant.openTime; // "10:00:00"
    const closeTime = selectedRestaurant.closeTime; // "23:00:00"

    if (!openTime || !closeTime) return [];

    // Parse opening and closing hours
    const openHour = parseInt(openTime.split(":")[0]);
    const closeHour = parseInt(closeTime.split(":")[0]);

    // Generate hourly slots from opening to closing (1 hour before closing) with range format
    for (let hour = openHour; hour < closeHour; hour++) {
      const currentHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const currentPeriod = hour >= 12 ? "pm" : "am";

      const nextHour = hour + 1;
      const displayNextHour =
        nextHour > 12 ? nextHour - 12 : nextHour === 0 ? 12 : nextHour;
      const nextPeriod = nextHour >= 12 ? "pm" : "am";

      slots.push(
        `${currentHour}${currentPeriod} to ${displayNextHour}${nextPeriod}`,
      );
    }

    return slots;
  };

  // Get available time slots based on selected restaurant from search form
  const getSelectedRestaurantForSlots = () => {
    if (!searchFormData.restaurant) return null;

    const matchedRestaurant = restaurants.find(
      (restaurant) =>
        restaurant?.name
          ?.toLowerCase()
          .includes(searchFormData.restaurant.toLowerCase()) ||
        restaurant?.address
          ?.toLowerCase()
          .includes(searchFormData.restaurant.toLowerCase()),
    );

    return matchedRestaurant;
  };

  const selectedRestaurantForSlots = getSelectedRestaurantForSlots();
  const timeSlots = generateTimeSlots(selectedRestaurantForSlots);

  // Function to check if restaurant is currently open based on time
  const isRestaurantCurrentlyOpen = (restaurant) => {
    // Always return true if restaurant is active - time-based checking removed
    // Booking availability will be checked when user selects date/time
    return restaurant.isActive === true;
  };

  // Function to fetch restaurant photos
  const fetchRestaurantPhotos = async (restaurantId) => {
    try {
      const response = await axios.get(
        `${APP_URLS.API}/restaurant-photos/${restaurantId}`,
      );
      if (Array.isArray(response.data) && response.data.length > 0) {
        // Store all photos for display
        setRestaurantPhotos((prev) => ({
          ...prev,
          [restaurantId]: response.data,
        }));
      } else {

      }
    } catch (error) {
      console.error(
        `Error fetching photos for restaurant ${restaurantId}:`,
        error,
      );
    }
  };

  // SIMPLE SCROLL RESTORATION: Check sessionStorage on mount
  useEffect(() => {
    fetchRestaurants();

    // Pre-populate search from BookingHomePage navigation
    if (location.state?.restaurantSearch) {
      const { location: loc, date, time, guests } = location.state.restaurantSearch;
      if (loc) {
        setSearchTerm(loc);
      }
      setSearchFormData(prev => ({
        ...prev,
        restaurant: loc || prev.restaurant,
        bookingDate: date || prev.bookingDate,
        timeSlot: time || prev.timeSlot,
        guests: guests?.toString() || prev.guests,
      }));
    }

    // Check URL parameters for shared restaurant link
    const params = new URLSearchParams(location.search);
    const restaurantIdFromUrl = params.get("id");

    if (restaurantIdFromUrl) {
      setFocusedRestaurantId(restaurantIdFromUrl);
      // Also scroll to the card after data loads
      setTimeout(() => {
        const card = cardRefs.current[restaurantIdFromUrl];
        if (card) {
          card.scrollIntoView({ behavior: "smooth", block: "center" });
          setHighlightedRestaurantId(restaurantIdFromUrl);
          setTimeout(() => setHighlightedRestaurantId(null), 3000);
        }
      }, 1000);
    } else {
      const focusId = sessionStorage.getItem("focusRestaurantId");
      if (focusId) {
        sessionStorage.removeItem("focusRestaurantId");
        setFocusedRestaurantId(focusId);
      }
    }
  }, [location.search]);

  // When focusedRestaurantId is set, scroll to that card
  useEffect(() => {
    if (!focusedRestaurantId) {
      return;
    }

    if (!restaurants || restaurants.length === 0) {
      return;
    }

    let attempts = 0;

    const attemptScroll = () => {
      const card = cardRefs.current[focusedRestaurantId];

      if (card) {
        card.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedRestaurantId(focusedRestaurantId);
        setTimeout(() => setHighlightedRestaurantId(null), 3000);
        unlockScroll();
      } else if (attempts < 50) {
        attempts++;
        setTimeout(attemptScroll, 100);
      } else {
        unlockScroll();
      }
    };

    attemptScroll();
  }, [focusedRestaurantId, restaurants, filteredRestaurants, unlockScroll]);

  useEffect(() => {
    // Ensure restaurants is an array before filtering
    if (!Array.isArray(restaurants)) {
      setFilteredRestaurants([]);
      return;
    }

    const filtered = restaurants.filter((restaurant) => {
      // Ensure restaurant object exists
      if (!restaurant) return false;

      // Safe property access
      const name = restaurant?.restaurant_name || restaurant?.name || "";
      const city = restaurant?.city || "";
      const state = restaurant?.state || "";
      const address = restaurant?.address || "";
      const seatingCapacity = restaurant?.seatingCapacity || restaurant?.people_capacity || 0;
      const cuisineType = restaurant?.cuisineType || restaurant?.cuisine_type || "";
      const restaurantType = restaurant?.restaurantType || restaurant?.restaurant_type || "";

      const safeSearchTerm = searchTerm || "";
      const safeFormRestaurant = searchFormData?.restaurant || "";

      const matchesSearchTermLocal = (text, term) => {
        if (!text) return false;
        text = text.toLowerCase();
        if (text === term) return true;
        const regex = new RegExp(`(^|[^a-z0-9])${term}([^a-z0-9]|$)`, 'i');
        return regex.test(text);
      };

      const lowerSafeSearchTerm = safeSearchTerm.toLowerCase();
      const lowerSafeFormRestaurant = safeFormRestaurant.toLowerCase();

      // 1. Text Search
      const matchesSearch =
        !safeSearchTerm ||
        (city && city.toLowerCase() === lowerSafeSearchTerm) ||
        (name && name.toLowerCase().includes(lowerSafeSearchTerm)) ||
        (city && city.toLowerCase().includes(lowerSafeSearchTerm)) ||
        (state && state.toLowerCase().includes(lowerSafeSearchTerm)) ||
        (address && matchesSearchTermLocal(address, lowerSafeSearchTerm));

      const matchesFormSearch =
        !safeFormRestaurant ||
        (city && city.toLowerCase() === lowerSafeFormRestaurant) ||
        (name && name.toLowerCase().includes(lowerSafeFormRestaurant)) ||
        (city && city.toLowerCase().includes(lowerSafeFormRestaurant)) ||
        (state && state.toLowerCase().includes(lowerSafeFormRestaurant)) ||
        (address && matchesSearchTermLocal(address, lowerSafeFormRestaurant));

      // 2. Capacity Search
      const requestedGuests = searchFormData?.guests ? parseInt(searchFormData.guests) : 0;
      const matchesCapacity = !requestedGuests || seatingCapacity >= requestedGuests;

      // 3. Multi-Cuisine Filter
      const matchesCuisines =
        selectedCuisines.length === 0 ||
        selectedCuisines.some(c => cuisineType.toLowerCase().includes(c.toLowerCase()));

      // 4. Food Type (Veg/Non-Veg)
      const matchesFoodType =
        foodTypeFilter === "All" ||
        (restaurantType && (
          restaurantType.toLowerCase().includes(foodTypeFilter.toLowerCase()) ||
          (foodTypeFilter === "Pure Veg" && restaurantType.toLowerCase().includes("veg")) ||
          (foodTypeFilter === "Non-Veg" && restaurantType.toLowerCase().includes("non"))
        ));

      return (
        matchesSearch &&
        matchesFormSearch &&
        matchesCapacity &&
        matchesCuisines &&
        matchesFoodType
      );
    });

    // Sort Logic
    const sorted = [...filtered].sort((a, b) => {
      const priceA = 500 + ((parseInt(a.restaurantId || a.restaurant_id) || 0) % 1500);
      const priceB = 500 + ((parseInt(b.restaurantId || b.restaurant_id) || 0) % 1500);

      if (sortBy === "popularity") return 0;
      if (sortBy === "priceLow") return priceA - priceB;
      if (sortBy === "priceHigh") return priceB - priceA;
      return 0;
    });

    setFilteredRestaurants(sorted);
  }, [
    restaurants,
    searchTerm,
    searchFormData?.restaurant,
    searchFormData?.guests,
    selectedCuisines,
    foodTypeFilter,
    sortBy
  ]);

  const fetchRestaurants = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${APP_URLS.API}/restaurants`);

      // Ensure response.data is an array and has valid data
      const restaurantData = Array.isArray(response.data) ? response.data : [];

      setRestaurants(restaurantData);

      // Fetch photos for each restaurant
      restaurantData.forEach((restaurant) => {
        if (restaurant.restaurantId) {
          fetchRestaurantPhotos(restaurant.restaurantId);
        }
      });
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      // Set empty array on error to prevent undefined issues
      setRestaurants([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestaurantClick = (restaurant = null) => {
    setSelectedRestaurant(restaurant);
    // Scroll to top on mobile when restaurant is selected
    if (isMobile) {
      setTimeout(() => {
        safeScrollToTop({ top: 0, behavior: "smooth" });
      }, 0);
    }
  };

  const handleBack = () => {
    setSelectedRestaurant(null);
    // Scroll to top on mobile when going back to list
    if (isMobile) {
      setTimeout(() => {
        safeScrollToTop({ top: 0, behavior: "smooth" });
      }, 0);
    }
  };

  const handleShare = (e, item, type = "restaurant") => {
    e.stopPropagation();
    const itemId = item.restaurantId || item.restaurant_id || item.id;
    const itemName = type === "restaurant" ? item.name : `Table T${item.tableNumber} at ${item.restaurantName}`;
    const itemLocation = type === "restaurant"
      ? (item.address || item.city || item.state || "Location unavailable")
      : (item.restaurantLocation || item.restaurantCity || "Location unavailable");

    const shareUrl = `${window.location.origin}/restaurants${type === "restaurant" ? `?id=${itemId}` : ""}`;
    const shareText = `Check out this amazing restaurant: ${itemName}!\n\n📍 Location: ${itemLocation}\n\nBook your table now on Trivana: ${shareUrl}`;

    if (navigator.share) {
      navigator.share({
        title: itemName,
        text: shareText,
        url: shareUrl
      }).catch((err) => {
        navigator.clipboard.writeText(shareText).then(() => toast.success("Details copied to clipboard!"));
      });
    } else {
      navigator.clipboard.writeText(shareText).then(() => {
        toast.success("Details copied to clipboard!");
      }).catch(() => {
        toast.error("Failed to copy details");
      });
    }
  };

  const handleDirections = (e, item, type = "restaurant") => {
    e.stopPropagation();
    const name = type === "restaurant" ? item.name : item.restaurantName;
    const address = type === "restaurant" ? (item.address || item.city) : (item.restaurantLocation || item.restaurantCity);

    // Format the query to ensure Google Maps finds the exact location
    const locationStr = address ? `${name}, ${address}` : name;

    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationStr)}`;
    window.open(url, "_blank");
  };

  const toggleFilter = (value, selectedList, setSelectedList) => {
    setSelectedList((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const handleClearFilters = () => {
    setSelectedCuisines([]);
    setSelectedRestaurantTypes([]);
    setFoodTypeFilter("All");
    setSearchTerm("");
    setSearchFormData((prev) => ({
      ...prev,
      restaurant: "",
      guests: "",
    }));
    toast.info("All filters cleared");
  };

  const handleSearchFormChange = (e) => {
    const { name, value } = e.target;
    setSearchFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };
      return newData;
    });

    // Clear error for this field when user starts typing
    if (searchErrors[name]) {
      setSearchErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateSearchForm = () => {
    const errors = {};

    if (!searchFormData.restaurant.trim()) {
      errors.restaurant = "Restaurant name or address is required";
    }

    if (
      !searchFormData.guests ||
      parseInt(searchFormData.guests) < 1 ||
      parseInt(searchFormData.guests) > 20
    ) {
      errors.guests = "Please enter number of guests (1-20)";
    }

    if (!searchFormData.bookingDate) {
      errors.bookingDate = "Booking date is required";
    } else {
      const selectedDate = new Date(searchFormData.bookingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        errors.bookingDate = "Booking date cannot be in the past";
      }
    }

    if (!searchFormData.timeSlot) {
      errors.timeSlot = "Please select a time slot";
    }

    setSearchErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRestaurantSearch = (e) => {
    e.preventDefault();

    // Trigger table search when form is submitted
    searchTables();
  };

  // Get user's current location for restaurants
  const handleNearMe = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setGettingLocation(true);
    toast.info("Getting your location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });

        try {
          // Use reverse geocoding to get location name
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&accept-language=en&lat=${latitude}&lon=${longitude}`,
          );
          const data = await response.json();

          const locationName =
            data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            data.address?.state ||
            "Your Location";
          const hasLocationMatch = restaurants.some((restaurant) =>
            doesRestaurantMatchLocation(restaurant, locationName),
          );

          if (hasLocationMatch) {
            setSearchTerm(locationName);
            setSearchFormData((prev) => ({
              ...prev,
              restaurant: locationName,
            }));
            toast.success(`Location found: ${locationName}`);
          } else {
            setSearchTerm("");
            setSearchFormData((prev) => ({
              ...prev,
              restaurant: "",
            }));
            toast.info(
              `Detected ${locationName}. Showing all available restaurants because no exact city match was found.`,
            );
          }
        } catch (error) {
          console.error("Error with reverse geocoding:", error);
          toast.warning("Location found, but couldn't get address details");
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        console.error("Geolocation error:", error);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error(
              "Location access denied. Please enable location permissions in your browser.",
            );
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out.");
            break;
          default:
            toast.error("An error occurred while getting your location.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  // Search tables based on guests and location
  const searchTables = async () => {
    const guests = parseInt(searchFormData.guests) || 0;
    const location = searchFormData.restaurant || "";

    // If no search criteria, show restaurants
    if (!guests && !location) {
      setShowTables(false);
      setSearchedTables([]);
      return;
    }

    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (guests > 0) params.append("guests", guests);
      if (location) params.append("location", location);

      const response = await axios.get(
        `${APP_URLS.API}/restaurants/search/tables?${params.toString()}`,
      );

      setSearchedTables(response.data);
      setShowTables(true);

      if (response.data.length === 0) {
        toast.info("No tables found matching your criteria");
      } else {
        toast.success(`Found ${response.data.length} available tables!`);
      }
    } catch (error) {
      console.error("Error searching tables:", error);
      toast.error("Failed to search tables");
      setSearchedTables([]);
      setShowTables(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    searchTables();
  };

  // Handle clearing search to show restaurants again
  const handleClearSearch = () => {
    setSearchFormData({
      restaurant: "",
      guests: "",
      bookingDate: "",
      timeSlot: "",
    });
    setShowTables(false);
    setSearchedTables([]);
  };

  const sliderSettings = {
    dots: !isMobile,
    infinite: true,
    speed: 1200,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
    fade: true,
    cssEase: "cubic-bezier(0.645, 0.045, 0.355, 1.000)",
    dotsClass: "slick-dots custom-dots",
  };

  const sliderImages = [
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?w=1200&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=1200&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1424847651672-bf20a4b0982b?w=1200&auto=format&fit=crop&q=80",
  ];

  if (selectedRestaurant) {
    return (
      <RestaurantDetail
        restaurant={selectedRestaurant}
        user={currentUser}
        onBack={handleBack}
        requestedGuests={
          searchFormData?.guests ? parseInt(searchFormData.guests) : 0
        }
      />
    );
  }

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#2E2E2E",
        background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        minHeight: "100vh",
        paddingTop: "90px",
      }}
    >
      {/* Hero Section with Image Slider */}
      <section
        style={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Beautiful Image Slider */}
        <div
          style={{
            position: "relative",
            borderRadius: "15px",
            margin: "10px",
            boxShadow: "0 15px 35px rgba(0,0,0,0.3)",
            overflow: "hidden",
          }}
        >
          <Slider {...sliderSettings}>
            {sliderImages.map((image, index) => (
              <div key={index}>
                <div
                  style={{
                    position: "relative",
                    height: isMobile ? "300px" : "500px",
                    background: `url(${image}) center/cover no-repeat`,
                    borderRadius: "15px",
                  }}
                >
                  {/* Dark Overlay for Better Text Readability */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)",
                    }}
                  />
                </div>
              </div>
            ))}
          </Slider>

          {/* Overlay Content - Title */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              zIndex: 2,
              width: "90%",
              maxWidth: "800px",
            }}
          >
            <h1
              style={{
                fontSize: isMobile ? "2rem" : "2.5rem",
                fontWeight: "700",
                color: "#FFFFFF",
                letterSpacing: "-0.5px",
                textShadow: "0 2px 12px rgba(0,0,0,0.4)",
                lineHeight: "1.2",
              }}
            >
              Discover Great Places to Eat
            </h1>
            <p
              style={{
                fontSize: isMobile ? "0.95rem" : "1.1rem",
                fontWeight: "400",
                color: "rgba(255,255,255,0.95)",
                marginTop: "8px",
                textShadow: "0 1px 8px rgba(0,0,0,0.3)",
              }}
            >
              Book the best restaurants with exclusive deals
            </p>
          </div>

          {/* Advanced Search Bar - Desktop Only Inside Slider */}
          {!isMobile && (
            <div
              style={{
                position: "absolute",
                bottom: "40px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 3,
                width: "90%",
                maxWidth: "1200px",
              }}
            >
              <div
                style={{
                  maxWidth: "1200px",
                  margin: "0 auto",
                }}
              >
                <form
                  className="restaurant-search-form"
                  onSubmit={handleRestaurantSearch}
                  style={{
                    background: "#FFFFFF",
                    borderRadius: isMobile ? "8px" : "12px",
                    padding: "0",
                    boxShadow:
                      "0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 20px rgba(0, 0, 0, 0.15)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "2.2fr 1.5fr 1.3fr 130px",
                      gap: isMobile ? "8px" : "0",
                      alignItems: "stretch",
                      padding: isMobile ? "12px" : "0",
                    }}
                  >
                    {/* Restaurant Location */}
                    <div
                      style={{
                        borderRight: isMobile ? "none" : "1px solid #E5E7EB",
                        borderBottom: isMobile ? "1px solid #E5E7EB" : "none",
                        padding: isMobile ? "12px 0" : "18px 24px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="text"
                          name="restaurant"
                          value={searchFormData.restaurant}
                          onChange={handleSearchFormChange}
                          placeholder="Search for restaurants..."
                          style={{
                            flex: 1,
                            padding: "0",
                            fontSize: isMobile ? "0.95rem" : "1.05rem",
                            border: "none",
                            background: "transparent",
                            color: "#1F2937",
                            outline: "none",
                            fontWeight: "500",
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleNearMe}
                          disabled={gettingLocation}
                          style={{
                            padding: "6px 10px",
                            background: gettingLocation
                              ? "#FEE2E2"
                              : "transparent",
                            border: "1px solid #E5E7EB",
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            color: gettingLocation ? "#991B1B" : "#EF4444",
                            cursor: gettingLocation ? "wait" : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                            transition: "all 0.2s ease",
                            opacity: gettingLocation ? 0.8 : 1,
                          }}
                          onMouseOver={(e) => {
                            if (!gettingLocation) {
                              e.target.style.background = "#FEF2F2";
                              e.target.style.borderColor = "#EF4444";
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!gettingLocation) {
                              e.target.style.background = "transparent";
                              e.target.style.borderColor = "#E5E7EB";
                            }
                          }}
                        >
                          <FaMapMarkerAlt
                            size={10}
                            style={{
                              animation: gettingLocation
                                ? "pulse 1.5s infinite"
                                : "none",
                            }}
                          />
                          {gettingLocation ? "Finding..." : "Near me"}
                        </button>
                      </div>
                      {searchErrors.restaurant && (
                        <p
                          style={{
                            color: "#EE2E24",
                            fontSize: "0.7rem",
                            marginTop: "4px",
                            fontWeight: "500",
                          }}
                        >
                          {searchErrors.restaurant}
                        </p>
                      )}
                    </div>

                    {/* Booking Date */}
                    <div
                      style={{
                        borderRight: isMobile ? "none" : "1px solid #E5E7EB",
                        borderBottom: isMobile ? "1px solid #E5E7EB" : "none",
                        padding: isMobile ? "12px 0" : "18px 24px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#6B7280",
                          marginBottom: "4px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Booking Date
                      </div>
                      <input
                        type="text"
                        placeholder="Fri, 17 Oct"
                        onFocus={(e) => {
                          e.target.type = "date";
                          e.target.min = new Date().toISOString().split("T")[0];
                        }}
                        onBlur={(e) => {
                          if (!e.target.value) e.target.type = "text";
                        }}
                        name="bookingDate"
                        value={searchFormData.bookingDate}
                        onChange={handleSearchFormChange}
                        style={{
                          width: "100%",
                          padding: "0",
                          fontSize: "1.05rem",
                          border: "none",
                          background: "transparent",
                          color: "#1F2937",
                          outline: "none",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      />
                    </div>

                    {/* Number of Guests */}
                    <div
                      style={{
                        borderRight: isMobile ? "none" : "1px solid #E5E7EB",
                        borderBottom: isMobile ? "1px solid #E5E7EB" : "none",
                        padding: isMobile ? "12px 0" : "18px 24px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#6B7280",
                          marginBottom: "4px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Guests
                      </div>
                      <input
                        type="number"
                        placeholder="Number of guests"
                        name="guests"
                        value={searchFormData.guests}
                        onChange={handleSearchFormChange}
                        min="1"
                        style={{
                          width: "100%",
                          padding: "0",
                          fontSize: "1.05rem",
                          border: "none",
                          background: "transparent",
                          color: "#1F2937",
                          outline: "none",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      />
                    </div>

                    {/* Search Button */}
                    <button
                      type="submit"
                      style={{
                        padding: isMobile ? "12px 0" : "0",
                        background:
                          "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                        color: "#FFFFFF",
                        border: "none",
                        fontSize: isMobile ? "0.95rem" : "0.95rem",
                        fontWeight: "700",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                        fontFamily: "'Inter', sans-serif",
                        boxShadow: "0 4px 12px rgba(238, 46, 36, 0.25)",
                        borderRadius: isMobile ? "8px" : "0",
                        width: isMobile ? "100%" : "auto",
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background =
                          "linear-gradient(135deg, #D02820 0%, #E5B000 100%)";
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow =
                          "0 6px 16px rgba(238, 46, 36, 0.35)";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background =
                          "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)";
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 4px 12px rgba(238, 46, 36, 0.25)";
                      }}
                    >
                      Search
                    </button>
                  </div>

                  {/* Popular Cuisines Section */}
                  <div
                    style={{
                      padding: "14px 24px",
                      background:
                        "linear-gradient(135deg, #2E2E2E 0%, #1A1A1A 100%)",
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      gap: isMobile ? "10px" : "16px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.9rem",
                        color: "#FFFFFF",
                        fontWeight: "600",
                        fontFamily: "'Inter', sans-serif",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Popular cuisines
                    </span>
                    <div
                      style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                    >
                      {["Italian", "Chinese", "Indian", "Mexican"].map(
                        (cuisine) => (
                          <button
                            key={cuisine}
                            type="button"
                            onClick={() => {
                              toggleFilter(cuisine, selectedCuisines, setSelectedCuisines);
                              toast.info(
                                selectedCuisines.includes(cuisine)
                                  ? "Cuisine filter cleared"
                                  : `Filtering ${cuisine} restaurants...`,
                              );
                            }}
                            style={{
                              padding: "6px 16px",
                              background:
                                selectedCuisines.includes(cuisine)
                                  ? "#EE2E24"
                                  : "#FFFFFF",
                              color:
                                selectedCuisines.includes(cuisine)
                                  ? "#FFFFFF"
                                  : "#2E2E2E",
                              border:
                                selectedCuisines.includes(cuisine)
                                  ? "1px solid #EE2E24"
                                  : "1px solid #E5E7EB",
                              borderRadius: "6px",
                              fontSize: "0.85rem",
                              fontWeight: "600",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              fontFamily: "'Inter', sans-serif",
                            }}
                            onMouseOver={(e) => {
                              if (!selectedCuisines.includes(cuisine)) {
                                e.target.style.background = "#F5F5F5";
                                e.target.style.borderColor = "#EE2E24";
                                e.target.style.color = "#EE2E24";
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!selectedCuisines.includes(cuisine)) {
                                e.target.style.background = "#FFFFFF";
                                e.target.style.borderColor = "#E5E7EB";
                                e.target.style.color = "#2E2E2E";
                              }
                            }}
                          >
                            {cuisine}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Mobile Search Section - Refined & Elegant */}
      {isMobile && (
        <section
          style={{
            padding: "8px",
            background: "transparent",
            marginTop: "-30px", // Pull up to overlap slightly with hero if needed, but let's keep it clean
            position: "relative",
            zIndex: 10,
          }}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "20px",
              padding: "16px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.04)",
            }}
          >
            <form onSubmit={handleRestaurantSearch}>
              <h3 
                style={{ 
                  fontSize: "1.1rem", 
                  fontWeight: "800", 
                  marginBottom: "14px", 
                  color: "#111827", 
                  textAlign: "center",
                  letterSpacing: "-0.02em"
                }}
              >
                Find Your Perfect Meal
              </h3>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {/* Search Input */}
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    background: "#F9FAFB",
                    borderRadius: "14px",
                    border: "1px solid #F3F4F6",
                    padding: "10px 14px",
                    transition: "all 0.2s ease",
                  }}
                >
                  <FaSearch style={{ color: "#EE2E24", fontSize: "14px", marginRight: "10px", opacity: 0.8 }} />
                  <input
                    type="text"
                    name="restaurant"
                    value={searchFormData.restaurant}
                    onChange={handleSearchFormChange}
                    placeholder="Search for restaurants..."
                    style={{
                      flex: 1,
                      border: "none",
                      background: "transparent",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      color: "#374151",
                      outline: "none",
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleNearMe}
                    disabled={gettingLocation}
                    style={{
                      padding: "6px 12px",
                      background: gettingLocation ? "#FEF2F2" : "#FFFFFF",
                      border: "1px solid #FEE2E2",
                      borderRadius: "10px",
                      fontSize: "0.7rem",
                      color: "#EE2E24",
                      fontWeight: "700",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      boxShadow: "0 2px 4px rgba(238, 46, 36, 0.05)",
                    }}
                  >
                    <FaMapMarkerAlt size={10} />
                    {gettingLocation ? "..." : "Near me"}
                  </button>
                </div>

                {/* Date & Guests Row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  {/* Date Selection */}
                  <div
                    style={{
                      background: "#F9FAFB",
                      borderRadius: "14px",
                      border: "1px solid #F3F4F6",
                      padding: "10px 14px",
                    }}
                  >
                    <span style={{ display: "block", fontSize: "0.65rem", fontWeight: "800", color: "#9CA3AF", textTransform: "uppercase", marginBottom: "2px" }}>
                      Date
                    </span>
                    <input
                      type="text"
                      placeholder="Select date"
                      onFocus={(e) => {
                        e.target.type = "date";
                        e.target.min = new Date().toISOString().split("T")[0];
                      }}
                      onBlur={(e) => {
                        if (!e.target.value) e.target.type = "text";
                      }}
                      name="bookingDate"
                      value={searchFormData.bookingDate}
                      onChange={handleSearchFormChange}
                      style={{
                        width: "100%",
                        border: "none",
                        background: "transparent",
                        fontSize: "0.85rem",
                        fontWeight: "700",
                        color: "#111827",
                        outline: "none",
                      }}
                    />
                  </div>

                  {/* Guests Selection */}
                  <div
                    style={{
                      background: "#F9FAFB",
                      borderRadius: "14px",
                      border: "1px solid #F3F4F6",
                      padding: "10px 14px",
                    }}
                  >
                    <span style={{ display: "block", fontSize: "0.65rem", fontWeight: "800", color: "#9CA3AF", textTransform: "uppercase", marginBottom: "2px" }}>
                      Guests
                    </span>
                    <input
                      type="number"
                      placeholder="No. of guests"
                      name="guests"
                      value={searchFormData.guests}
                      onChange={handleSearchFormChange}
                      min="1"
                      style={{
                        width: "100%",
                        border: "none",
                        background: "transparent",
                        fontSize: "0.85rem",
                        fontWeight: "700",
                        color: "#111827",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>

                {/* Search Button - Refined & High-Fidelity */}
                <button
                  type="submit"
                  style={{
                    marginTop: "6px",
                    background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: "18px",
                    padding: "16px",
                    fontSize: "0.95rem",
                    fontWeight: "900",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    boxShadow: "0 10px 25px rgba(238, 46, 36, 0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    cursor: "pointer"
                  }}
                >
                  <FaSearch size={14} />
                  Find Your Table
                </button>

                {/* Popular Cuisines Pills */}
                <div style={{ marginTop: "12px" }}>
                  <p style={{ fontSize: "0.7rem", fontWeight: "800", color: "#9CA3AF", textAlign: "center", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.02em" }}>
                    Popular Cuisines
                  </p>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
                    {["Italian", "Chinese", "Indian", "Mexican"].map((cuisine) => (
                      <button
                        key={cuisine}
                        type="button"
                        onClick={() => {
                          toggleFilter(cuisine, selectedCuisines, setSelectedCuisines);
                        }}
                        style={{
                          padding: "6px 14px",
                          background: selectedCuisines.includes(cuisine) ? "#EE2E24" : "#F3F4F6",
                          color: selectedCuisines.includes(cuisine) ? "#FFFFFF" : "#4B5563",
                          border: "none",
                          borderRadius: "20px",
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {cuisine}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* Restaurants List Section - MMT Inspired Layout */}
      {!selectedRestaurant && (
        <section
          style={{
            padding: isMobile ? "10px 0" : "20px 0",
            background: "#f2f2f2",
            minHeight: "100vh",
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto", padding: isMobile ? "0 6px" : "0 10px", display: "flex", gap: "20px", flexDirection: isMobile ? "column" : "row" }}>
            {/* Sidebar Filters */}
            {!isMobile && (
              <div style={{ width: "260px", flexShrink: 0 }}>
                {/* Map Widget */}
                <div
                  onClick={() => {
                    const mapQueryBase = (searchTerm || searchFormData.restaurant || filteredRestaurants?.[0]?.city || "Restaurants").trim();
                    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${mapQueryBase} restaurants`)}`, '_blank');
                  }}
                  style={{ background: "#fff", padding: "10px", borderRadius: "8px", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", marginBottom: "20px", position: "relative", cursor: "pointer", overflow: "hidden" }}
                >
                  <div
                    style={{
                      height: "110px",
                      backgroundImage: "url('https://media.wired.com/photos/59269cd37034dc5f91bec0f1/master/pass/GoogleMapTA.jpg')",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      borderRadius: "4px",
                    }}
                  />
                  <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "100%", padding: "0 20px" }}>
                    <button style={{ width: "100%", padding: "10px", background: "#fff", color: "#0095f6", fontWeight: "700", border: "none", borderRadius: "6px", fontSize: "0.9rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", boxShadow: "0 4px 10px rgba(0,0,0,0.15)" }}>
                      EXPLORE ON MAP <FaMapMarkerAlt size={14} />
                    </button>
                  </div>
                </div>

                <div style={{ background: "#fff", padding: "20px", borderRadius: "16px", border: "1px solid #f0f0f0", boxShadow: "0 10px 30px rgba(0,0,0,0.04)", position: "sticky", top: "20px", maxHeight: "calc(100vh - 40px)", overflowY: "auto", scrollbarWidth: "none" }}>
                  <style>
                    {`
                      div::-webkit-scrollbar {
                        display: none;
                      }
                    `}
                  </style>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <h3 style={{ fontSize: "1.1rem", fontWeight: "800", color: "#1a1a1a", margin: 0 }}>Filters</h3>
                    <button onClick={handleClearFilters} style={{ background: "transparent", border: "none", fontSize: "0.8rem", color: "#EE2E24", fontWeight: "700", cursor: "pointer", textTransform: "uppercase" }}>Clear All</button>
                  </div>

                  {/* Food Type */}
                  <div style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
                    <h4 style={{ fontSize: "0.95rem", fontWeight: "700", marginBottom: "12px", color: "#333" }}>Dietary Preference</h4>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {["All", "Pure Veg", "Non-Veg"].map((type) => (
                        <label key={type} style={{ display: "flex", alignItems: "center", cursor: "pointer", padding: "4px 0" }}>
                          <input
                            type="radio"
                            name="foodType"
                            checked={foodTypeFilter === type}
                            onChange={() => setFoodTypeFilter(type)}
                            style={{ accentColor: "#EE2E24", width: "16px", height: "16px" }}
                          />
                          <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#555", fontWeight: "500" }}>{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Cuisine Type */}
                  <div style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
                    <h4 style={{ fontSize: "0.95rem", fontWeight: "700", marginBottom: "12px", color: "#333" }}>Cuisine</h4>
                    {["Indian", "Chinese", "Italian", "Mexican", "Japanese", "Continental"].map((cuisine, idx) => (
                      <label key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                        <input
                          type="checkbox"
                          checked={selectedCuisines.includes(cuisine)}
                          onChange={() => toggleFilter(cuisine, selectedCuisines, setSelectedCuisines)}
                          style={{ accentColor: "#EE2E24", width: "16px", height: "16px" }}
                        />
                        <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#555", fontWeight: "500" }}>{cuisine}</span>
                      </label>
                    ))}
                  </div>

                  {/* Restaurant Type */}
                  <div>
                    <h4 style={{ fontSize: "0.95rem", fontWeight: "700", marginBottom: "12px", color: "#333" }}>Establishment Type</h4>
                    {["Fine Dining", "Cafe", "Casual Dining", "Quick Service", "Bakery"].map((type, idx) => (
                      <label key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                        <input
                          type="checkbox"
                          checked={selectedRestaurantTypes.includes(type)}
                          onChange={() => toggleFilter(type, selectedRestaurantTypes, setSelectedRestaurantTypes)}
                          style={{ accentColor: "#EE2E24", width: "16px", height: "16px" }}
                        />
                        <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#555", fontWeight: "500" }}>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Main Content Area */}
            <div style={{ flex: 1 }}>

              {/* Mobile Filter Trigger - Refined & Elegant */}
              {isMobile && (
                <div style={{ marginBottom: "14px", display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => setShowMobileFilters(true)}
                    style={{
                      flex: 1,
                      padding: "12px 16px",
                      borderRadius: "14px",
                      border: "1px solid #F3F4F6",
                      background: "#FFFFFF",
                      color: "#111827",
                      fontWeight: "900",
                      fontSize: "0.8rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      cursor: "pointer"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", color: "#EE2E24" }}>
                         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                      </span>
                      <span>Filters</span>
                    </div>
                    {activeFilterCount > 0 && (
                      <span style={{ background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)", color: "#FFFFFF", minWidth: "22px", height: "22px", borderRadius: "22px", fontSize: "0.7rem", fontWeight: "900", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px", boxShadow: "0 4px 10px rgba(238, 46, 36, 0.2)" }}>
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>
              )}

              {/* Mobile Filter Drawer - Refined & Elegant */}
              {isMobile && showMobileFilters && (
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(15, 23, 42, 0.4)",
                    backdropFilter: "blur(4px)",
                    zIndex: 1200,
                    display: "flex",
                    justifyContent: "flex-end"
                  }}
                  onClick={() => setShowMobileFilters(false)}
                >
                  <div
                    style={{
                      width: "85%",
                      maxWidth: "340px",
                      height: "100%",
                      background: "#FFFFFF",
                      boxShadow: "-10px 0 50px rgba(0,0,0,0.15)",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "24px 0 0 24px"
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ padding: "20px", borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <h3 style={{ fontSize: "1.1rem", fontWeight: "900", color: "#111827", margin: 0 }}>Filters</h3>
                        <p style={{ fontSize: "0.65rem", fontWeight: "800", color: "#9CA3AF", textTransform: "uppercase", marginTop: "2px", letterSpacing: "0.05em" }}>Customize your search</p>
                      </div>
                      <button
                        onClick={() => setShowMobileFilters(false)}
                        style={{ border: "none", background: "#F3F4F6", color: "#6B7280", width: "32px", height: "32px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", cursor: "pointer" }}
                      >
                        ✕
                      </button>
                    </div>

                    <div style={{ flex: 1, overflowY: "auto", padding: "20px", background: "#FBFBFC" }}>
                      <div style={{ background: "#FFFFFF", padding: "16px", borderRadius: "16px", border: "1px solid #F3F4F6", marginBottom: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                          <h4 style={{ fontSize: "0.85rem", fontWeight: "900", margin: 0, color: "#111827", textTransform: "uppercase", letterSpacing: "0.02em" }}>Dietary</h4>
                          <button onClick={handleClearFilters} style={{ background: "transparent", border: "none", fontSize: "0.65rem", color: "#EE2E24", fontWeight: "900", cursor: "pointer", textTransform: "uppercase" }}>Clear</button>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {["All", "Pure Veg", "Non-Veg"].map((type) => (
                            <label key={type} style={{ display: "flex", alignItems: "center", cursor: "pointer", padding: "4px 0" }}>
                              <input
                                type="radio"
                                name="foodTypeMobile"
                                checked={foodTypeFilter === type}
                                onChange={() => setFoodTypeFilter(type)}
                                style={{ accentColor: "#EE2E24", width: "18px", height: "18px" }}
                              />
                              <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#374151", fontWeight: "700" }}>{type}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={{ background: "#FFFFFF", padding: "16px", borderRadius: "16px", border: "1px solid #F3F4F6", marginBottom: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                        <h4 style={{ fontSize: "0.85rem", fontWeight: "900", margin: "0 0 12px 0", color: "#111827", textTransform: "uppercase", letterSpacing: "0.02em" }}>Cuisines</h4>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {["Indian", "Chinese", "Italian", "Mexican", "Japanese", "Continental"].map((cuisine, idx) => (
                            <label key={idx} style={{ display: "flex", alignItems: "center", cursor: "pointer", padding: "4px 0" }}>
                              <input
                                type="checkbox"
                                checked={selectedCuisines.includes(cuisine)}
                                onChange={() => toggleFilter(cuisine, selectedCuisines, setSelectedCuisines)}
                                style={{ accentColor: "#EE2E24", width: "18px", height: "18px" }}
                              />
                              <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#374151", fontWeight: "700" }}>{cuisine}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={{ background: "#FFFFFF", padding: "16px", borderRadius: "16px", border: "1px solid #F3F4F6", marginBottom: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                        <h4 style={{ fontSize: "0.85rem", fontWeight: "900", margin: "0 0 12px 0", color: "#111827", textTransform: "uppercase", letterSpacing: "0.02em" }}>Establishment</h4>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {["Fine Dining", "Cafe", "Casual Dining", "Quick Service", "Bakery"].map((type, idx) => (
                            <label key={idx} style={{ display: "flex", alignItems: "center", cursor: "pointer", padding: "4px 0" }}>
                              <input
                                type="checkbox"
                                checked={selectedRestaurantTypes.includes(type)}
                                onChange={() => toggleFilter(type, selectedRestaurantTypes, setSelectedRestaurantTypes)}
                                style={{ accentColor: "#EE2E24", width: "18px", height: "18px" }}
                              />
                              <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#374151", fontWeight: "700" }}>{type}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div style={{ padding: "16px 20px", borderTop: "1px solid #F3F4F6", background: "#FFFFFF", display: "flex", gap: "10px" }}>
                      <button
                        onClick={handleClearFilters}
                        style={{
                          flex: 1,
                          padding: "14px",
                          borderRadius: "14px",
                          border: "1px solid #F3F4F6",
                          background: "#FFFFFF",
                          color: "#6B7280",
                          fontWeight: "900",
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em"
                        }}
                      >
                        Reset
                      </button>
                      <button
                        onClick={() => setShowMobileFilters(false)}
                        style={{
                          flex: 1.5,
                          padding: "14px",
                          borderRadius: "14px",
                          border: "none",
                          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                          color: "#FFFFFF",
                          fontWeight: "900",
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          boxShadow: "0 8px 20px rgba(238, 46, 36, 0.2)"
                        }}
                      >
                        Show Results
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results Header & Sort */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? "12px" : "20px", flexWrap: "wrap", gap: "8px" }}>
                <h2 style={{ fontSize: isMobile ? "1rem" : "1.5rem", fontWeight: "700", color: "#1a1a1a", margin: 0 }}>
                  {filteredRestaurants.length} Restaurants Found
                  {(searchTerm || searchFormData.restaurant) && <span style={{ fontWeight: "400", color: "#666", fontSize: isMobile ? "0.85rem" : "1rem", marginLeft: "8px" }}>in {searchTerm || searchFormData.restaurant}</span>}
                </h2>
              </div>

              {/* Sort Bar - Standardized to Hotel Page Style */}
              <div style={{ background: "#fff", padding: isMobile ? "8px 10px" : "12px 20px", borderRadius: "12px", marginBottom: isMobile ? "12px" : "20px", display: "flex", alignItems: "center", boxShadow: "0 4px 15px rgba(0,0,0,0.03)", gap: isMobile ? "6px" : "25px", border: "1px solid #f5f5f5", overflowX: "auto" }}>
                <span style={{ fontSize: "0.85rem", fontWeight: "800", color: "#777", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap", display: isMobile ? "none" : "inline" }}>Sort By:</span>
                <div style={{ display: "flex", gap: isMobile ? "4px" : "10px", flex: 1, overflowX: "auto", scrollbarWidth: "none" }}>
                  {[
                    { label: "Popularity", value: "popularity" },
                    { label: "Price (Low to High)", value: "priceLow" },
                    { label: "Price (High to Low)", value: "priceHigh" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      style={{
                        padding: isMobile ? "5px 10px" : "6px 16px",
                        borderRadius: "20px",
                        border: "none",
                        background: sortBy === option.value ? "linear-gradient(90deg, #EE2E24, #FFC400)" : "transparent",
                        color: sortBy === option.value ? "#fff" : "#555",
                        fontSize: isMobile ? "0.72rem" : "0.85rem",
                        fontWeight: "700",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {isLoading ? (
                <div style={{ textAlign: "center", padding: "100px 0" }}>
                  <div className="res-loading-spinner" style={{ width: "40px", height: "40px", border: "3px solid #f3f3f3", borderTop: "3px solid #EE2E24", borderRadius: "50%", display: "inline-block" }}></div>
                  <p style={{ marginTop: "15px", fontWeight: "600", color: "#666" }}>Finding best restaurants for you...</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  {/* Show Clear Search button when tables are displayed */}
                  {showTables && (
                    <button
                      onClick={handleClearSearch}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      style={{
                        alignSelf: "flex-start",
                        marginBottom: "20px",
                        marginLeft: "10px",
                      }}
                    >
                      ← Back to Restaurants
                    </button>
                  )}

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: isMobile ? "12px" : "25px",
                      padding: isMobile ? "0 2px" : "0 10px",
                      width: "100%",
                    }}
                  >
                    {/* Display Tables when search is active */}
                    {showTables ? (
                      searchedTables && searchedTables.length > 0 ? (
                        searchedTables.map((table) => {
                          const restaurantId = table.restaurantId || table.restaurant_id;
                          const price = table.pricePerPerson || 500 + ((parseInt(restaurantId) || 0) % 1500);

                          return (
                            <div
                              key={table.tableId}
                              ref={(el) => (cardRefs.current[table.tableId] = el)}
                              onClick={() => {
                                const restaurant = {
                                  restaurantId: table.restaurantId,
                                  name: table.restaurantName,
                                  location: table.restaurantLocation,
                                  address: table.restaurantAddress,
                                  city: table.restaurantCity,
                                  open_time: table.openTime,
                                  close_time: table.closeTime,
                                  pricePerPerson: table.pricePerPerson,
                                  isActive: true,
                                };
                                setSelectedRestaurant(restaurant);
                              }}
                              style={{
                                display: "flex",
                                flexDirection: isMobile ? "column" : "row",
                                background: "#fff",
                                borderRadius: "16px",
                                border: "1px solid #f0f0f0",
                                overflow: "hidden",
                                transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                                cursor: "pointer",
                                minHeight: isMobile ? "auto" : "220px",
                                marginBottom: "16px",
                                position: "relative"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.06)";
                                e.currentTarget.style.borderColor = "#eee";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.borderColor = "#f0f0f0";
                              }}
                            >
                              {/* Left: Featured Image Section */}
                              <div style={{
                                width: isMobile ? "100%" : "280px",
                                minHeight: isMobile ? "185px" : "100%",
                                position: "relative",
                                background: "#f0f0f0",
                                overflow: "hidden"
                              }}>
                                <img
                                  src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                                  alt={table.restaurantName}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
                                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                                />
                                <div style={{
                                  position: "absolute",
                                  top: "12px",
                                  left: "12px",
                                  background: "rgba(255, 255, 255, 0.9)",
                                  padding: "4px 10px",
                                  borderRadius: "6px",
                                  fontSize: "0.65rem",
                                  fontWeight: "900",
                                  color: "#EE2E24",
                                  backdropFilter: "blur(4px)"
                                }}>
                                  VERIFIED
                                </div>
                                <div style={{ position: "absolute", bottom: "12px", left: "12px", background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", color: "#fff", padding: "4px 8px", borderRadius: "10px", fontSize: "0.65rem", fontWeight: "700" }}>
                                  {table.seatingCapacity} SEATS
                                </div>
                              </div>

                              {/* Middle: Details Section */}
                              <div style={{ flex: 1, padding: isMobile ? "12px 14px" : "20px", borderRight: isMobile ? "none" : "1px solid #f0f0f0", display: "flex", flexDirection: "column" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                  <div>
                                    <h3 style={{ fontSize: isMobile ? "0.95rem" : "1.2rem", fontWeight: "900", marginBottom: "4px", color: "#1a1a1a", letterSpacing: "-0.5px" }}>Table T{table.tableNumber} - {table.restaurantName}</h3>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: isMobile ? "8px" : "12px", color: "#666", fontSize: "0.8rem", fontWeight: "500" }}>
                                      <FaMapMarkerAlt style={{ color: "#EE2E24" }} size={12} />
                                      <span>{table.restaurantLocation || table.restaurantCity || "Location unavailable"}</span>
                                    </div>
                                  </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((table.restaurantName || "") + " " + (table.restaurantLocation || table.restaurantCity || ""))}`, '_blank');
                                    }}
                                    style={{
                                      display: "flex", alignItems: "center", gap: "6px",
                                      padding: "5px 12px", borderRadius: "20px",
                                      border: "1.5px solid #EE2E24", background: "#fff",
                                      color: "#EE2E24", fontSize: "0.75rem", fontWeight: "800",
                                      cursor: "pointer", whiteSpace: "nowrap"
                                    }}
                                  >
                                    <FaMapMarkerAlt size={11} /> View on Map
                                  </button>
                                </div>

                                <div style={{ marginTop: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                  {table.seatingCapacity && (
                                    <span style={{ fontSize: "0.7rem", color: "#555", fontWeight: "700", background: "#f0f0f0", padding: "4px 10px", borderRadius: "20px", border: "1px solid #e5e5e5" }}>
                                      👥 {table.seatingCapacity} Seats
                                    </span>
                                  )}
                                  {table.openTime && (
                                    <span style={{ fontSize: "0.7rem", color: "#555", fontWeight: "700", background: "#f0f0f0", padding: "4px 10px", borderRadius: "20px", border: "1px solid #e5e5e5" }}>
                                      🕐 {table.openTime} – {table.closeTime}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Right: Booking Section */}
                              <div style={{ width: isMobile ? "100%" : "220px", padding: isMobile ? "10px 14px" : "20px", display: "flex", flexDirection: isMobile ? "row" : "column", justifyContent: isMobile ? "space-between" : "center", alignItems: isMobile ? "center" : "flex-end", background: isMobile ? "#fafafa" : "#fafafa", borderLeft: isMobile ? "none" : "1px solid #f5f5f5", borderTop: isMobile ? "1px solid #f0f0f0" : "none" }}>
                                <div style={{ textAlign: isMobile ? "left" : "right", marginBottom: isMobile ? "0" : "15px" }}>
                                  <div style={{ color: "#EE2E24", fontWeight: "900", fontSize: isMobile ? "0.8rem" : "0.9rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    Check Rates
                                  </div>
                                </div>

                                <div style={{ width: isMobile ? "auto" : "100%" }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/restaurant-booking/${table.restaurantId}/${table.tableId}`, { state: { table } });
                                    }}
                                    style={{
                                      width: "100%",
                                      padding: isMobile ? "8px 16px" : "12px",
                                      background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "12px",
                                      fontWeight: "900",
                                      cursor: "pointer",
                                      fontSize: isMobile ? "0.75rem" : "0.85rem",
                                      textTransform: "uppercase",
                                      marginBottom: isMobile ? "0" : "12px",
                                      boxShadow: "0 6px 15px rgba(238, 46, 36, 0.2)",
                                      transition: "all 0.3s ease",
                                      letterSpacing: "0.5px",
                                      whiteSpace: "nowrap"
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
                                    onMouseLeave={e => e.currentTarget.style.filter = "none"}
                                  >
                                    View Deals
                                  </button>

                                  <div style={{ display: isMobile ? "none" : "flex", gap: "10px", justifyContent: "flex-end", marginTop: "12px" }}>
                                    <button
                                      onClick={(e) => handleShare(e, table, "table")}
                                      style={{ background: "#fff", border: "1px solid #ddd", borderRadius: "50%", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#555" }}
                                      title="Share"
                                    >
                                      <FaShareAlt size={14} />
                                    </button>
                                    <button
                                      onClick={(e) => handleDirections(e, table, "table")}
                                      style={{ background: "#fff", border: "1px solid #ddd", borderRadius: "50%", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#555" }}
                                      title="Directions"
                                    >
                                      <FaDirections size={14} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ textAlign: "center", padding: "40px", background: "#fff", borderRadius: "16px", color: "#666" }}>
                          <p style={{ fontSize: "1.1rem", fontWeight: "500" }}>No tables found matching your search criteria.</p>
                        </div>
                      )
                    ) : (
                      /* Display Restaurants when no search is active */
                      filteredRestaurants && filteredRestaurants.length > 0 ? (
                        filteredRestaurants.map((restaurant) => {
                          const restaurantId = restaurant.restaurantId || restaurant.restaurant_id || restaurant.id;
                          const price = restaurant.pricePerPerson || 800 + ((parseInt(restaurantId) || 0) % 2000);

                          return (
                            <div
                              key={String(restaurantId)}
                              ref={(el) => (cardRefs.current[restaurantId] = el)}
                              onClick={() => handleRestaurantClick(restaurant)}
                              className="restaurant-card-row"
                              style={{
                                display: "flex",
                                flexDirection: isMobile ? "column" : "row",
                                background: "#fff",
                                borderRadius: "16px",
                                border: "1px solid #f0f0f0",
                                overflow: "hidden",
                                transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                                cursor: "pointer",
                                minHeight: isMobile ? "auto" : "220px",
                                marginBottom: "16px",
                                position: "relative"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.06)";
                                e.currentTarget.style.borderColor = "#eee";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.borderColor = "#f0f0f0";
                              }}
                            >
                              {/* Left: Featured Image Section */}
                              <div className="restaurant-card-image-wrap" style={{
                                width: isMobile ? "100%" : "280px",
                                minHeight: isMobile ? "185px" : "100%",
                                position: "relative",
                                background: "#f0f0f0",
                                overflow: "hidden"
                              }}>
                                <img
                                  src={(() => {
                                    const dbPhoto = restaurantPhotos[restaurantId]?.[0]?.photoUrl;
                                    return dbPhoto || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                                  })()}
                                  alt={restaurant.name}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
                                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                                />
                                <div style={{
                                  position: "absolute",
                                  top: "12px",
                                  left: "12px",
                                  background: "rgba(255, 255, 255, 0.9)",
                                  padding: "4px 10px",
                                  borderRadius: "6px",
                                  fontSize: "0.65rem",
                                  fontWeight: "900",
                                  color: "#EE2E24",
                                  backdropFilter: "blur(4px)"
                                }}>
                                  VERIFIED
                                </div>
                                <div style={{ position: "absolute", bottom: "12px", left: "12px", background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", color: "#fff", padding: "4px 8px", borderRadius: "10px", fontSize: "0.65rem", fontWeight: "700" }}>
                                  {restaurantPhotos[restaurantId]?.length || 4} PHOTOS
                                </div>
                              </div>

                              {/* Middle: Details Section */}
                              <div className="restaurant-card-details" style={{ flex: 1, padding: isMobile ? "12px 14px" : "20px", borderRight: isMobile ? "none" : "1px solid #f0f0f0", display: "flex", flexDirection: "column" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                  <div>
                                    <h3 style={{ fontSize: isMobile ? "0.95rem" : "1.2rem", fontWeight: "900", marginBottom: "4px", color: "#1a1a1a", letterSpacing: "-0.5px" }}>{restaurant.name}</h3>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: isMobile ? "8px" : "12px", color: "#666", fontSize: "0.8rem", fontWeight: "500" }}>
                                      <FaMapMarkerAlt style={{ color: "#EE2E24" }} size={12} />
                                      <span>{restaurant.address || restaurant.city || restaurant.state || "Location unavailable"}</span>
                                    </div>
                                  </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((restaurant.name || "") + " " + (restaurant.address || restaurant.city || ""))}`, '_blank');
                                    }}
                                    style={{
                                      display: "flex", alignItems: "center", gap: "6px",
                                      padding: "5px 12px", borderRadius: "20px",
                                      border: "1.5px solid #EE2E24", background: "#fff",
                                      color: "#EE2E24", fontSize: "0.75rem", fontWeight: "800",
                                      cursor: "pointer", whiteSpace: "nowrap"
                                    }}
                                  >
                                    <FaMapMarkerAlt size={11} /> View on Map
                                  </button>
                                </div>

                                <div style={{ marginTop: "auto", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                  {(restaurant.cuisineType || restaurant.cuisine_type || restaurant.cuisine) && (
                                    <span style={{ fontSize: "0.7rem", color: "#555", fontWeight: "700", background: "#f0f0f0", padding: "4px 10px", borderRadius: "20px", border: "1px solid #e5e5e5" }}>
                                      🍽️ {restaurant.cuisineType || restaurant.cuisine_type || restaurant.cuisine}
                                    </span>
                                  )}
                                  {(restaurant.restaurantType || restaurant.restaurant_type) && (() => {
                                    const rType = restaurant.restaurantType || restaurant.restaurant_type;
                                    const isVeg = rType.toLowerCase().includes("veg") && !rType.toLowerCase().includes("non");
                                    return (
                                      <span style={{ fontSize: "0.7rem", color: "#fff", fontWeight: "700", background: isVeg ? "#27ae60" : "#e74c3c", padding: "4px 10px", borderRadius: "20px", border: "none" }}>
                                        {isVeg ? "🟢" : "🔴"} {rType}
                                      </span>
                                    );
                                  })()}
                                  {(restaurant.seatingCapacity || restaurant.people_capacity) > 0 && (
                                    <span style={{ fontSize: "0.7rem", color: "#555", fontWeight: "700", background: "#f0f0f0", padding: "4px 10px", borderRadius: "20px", border: "1px solid #e5e5e5" }}>
                                      👥 {restaurant.seatingCapacity || restaurant.people_capacity} Seats
                                    </span>
                                  )}
                                  {restaurant.open_time && (
                                    <span style={{ fontSize: "0.7rem", color: "#555", fontWeight: "700", background: "#f0f0f0", padding: "4px 10px", borderRadius: "20px", border: "1px solid #e5e5e5" }}>
                                      🕐 {restaurant.open_time} – {restaurant.close_time}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Right: Booking Section */}
                              <div className="restaurant-card-booking" style={{ width: isMobile ? "100%" : "220px", padding: isMobile ? "10px 14px" : "20px", display: "flex", flexDirection: isMobile ? "row" : "column", justifyContent: isMobile ? "space-between" : "center", alignItems: isMobile ? "center" : "flex-end", background: "#fafafa", borderLeft: isMobile ? "none" : "1px solid #f5f5f5", borderTop: isMobile ? "1px solid #f0f0f0" : "none" }}>
                                <div style={{ textAlign: isMobile ? "left" : "right", marginBottom: isMobile ? "0" : "15px" }}>
                                  <div style={{ color: "#EE2E24", fontWeight: "900", fontSize: isMobile ? "0.8rem" : "0.9rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    Check Rates
                                  </div>
                                </div>

                                <div style={{ width: isMobile ? "auto" : "100%" }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRestaurantClick(restaurant);
                                    }}
                                    style={{
                                      width: "100%",
                                      padding: isMobile ? "8px 16px" : "12px",
                                      background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "12px",
                                      fontWeight: "900",
                                      cursor: "pointer",
                                      fontSize: isMobile ? "0.75rem" : "0.85rem",
                                      textTransform: "uppercase",
                                      marginBottom: isMobile ? "0" : "12px",
                                      boxShadow: "0 6px 15px rgba(238, 46, 36, 0.2)",
                                      transition: "all 0.3s ease",
                                      letterSpacing: "0.5px",
                                      whiteSpace: "nowrap"
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
                                    onMouseLeave={e => e.currentTarget.style.filter = "none"}
                                  >
                                    View Deals
                                  </button>

                                  <div style={{ display: isMobile ? "none" : "flex", gap: "10px", justifyContent: "flex-end", marginTop: "12px" }}>
                                    <button
                                      onClick={(e) => handleShare(e, restaurant, "restaurant")}
                                      style={{ background: "transparent", border: "1px solid #ddd", borderRadius: "50%", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#555" }}
                                      title="Share"
                                    >
                                      <FaShareAlt size={14} />
                                    </button>
                                    <button
                                      onClick={(e) => handleDirections(e, restaurant, "restaurant")}
                                      style={{ background: "transparent", border: "1px solid #ddd", borderRadius: "50%", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#555" }}
                                      title="Directions"
                                    >
                                      <FaDirections size={14} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div
                          style={{
                            gridColumn: "1 / -1",
                            textAlign: "center",
                            padding: "60px 20px",
                            color: "#9CA3AF",
                            fontSize: "1.1rem",
                          }}
                        >
                          <div style={{ fontSize: "3rem", marginBottom: "15px" }}>🔍</div>
                          <div style={{ fontWeight: "600", marginBottom: "8px" }}>No restaurants found</div>
                          <div style={{ fontSize: "0.9rem" }}>Try adjusting your search criteria</div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .res-loading-spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default RestaurantsPage;
